import type { ReactNode } from 'react'
import cx from 'classnames'
import { isSupported, copy } from 'clipbrd'
import { Button } from '@/components/Button'
import { Avatar, type AvatarSubject } from '@/components/Avatar'
import {
  ListItem,
  ListItemContent,
  ListItemDescription,
  ListItemTitle,
  ListItemVisual,
} from '@/components/ListItem'
import { dateFormatters } from '@/utils/date-util'
import styles from './ClientUserDetailsInformation.module.css'

export interface ClientUserDetailsInformationProps {
  displayName: string
  email: string
  phone: string
  lastSeen: Date | null
  dateModified: Date
  dateCreated: Date
}

export function ClientUserDetailsInformation({
  displayName,
  email,
  phone,
  lastSeen,
  dateModified,
  dateCreated,
}: ClientUserDetailsInformationProps) {
  const copyTextToClipboard = (text: string) => {
    if (isSupported()) {
      copy(text)
    }
  }

  return (
    <UserInfoCard title="User information">
      <UserInfoLine title="Full name" value={displayName} />
      <UserInfoLine
        title="Email address"
        value={email}
        buttonText="Copy"
        buttonAction={() => copyTextToClipboard(email)}
      />
      <UserInfoLine
        title="Phone"
        value={phone}
        buttonText="Copy"
        buttonAction={() => copyTextToClipboard(phone)}
      />
      {lastSeen && (
        <UserInfoLine
          title="Last login"
          value={dateFormatters.dateLongWithTime.format(lastSeen)}
        />
      )}
      <UserInfoLine
        title="Last user update"
        value={dateFormatters.dateLongWithTime.format(dateModified)}
      />
      <UserInfoLine
        title="Date joined"
        value={dateFormatters.dateLongWithTime.format(dateCreated)}
      />
    </UserInfoCard>
  )
}

export interface ClientUserAdvisorsProps {
  /**
   * The advisor rows.
   */
  children: ReactNode
  /**
   * Whether the client has no advisors, which shows an empty state instead.
   */
  isEmpty: boolean
}

/**
 * The card listing the advisors assigned to the client.
 */
export function ClientUserAdvisors({
  children,
  isEmpty,
}: ClientUserAdvisorsProps) {
  return (
    <UserInfoCard title="Advisors" listContent={!isEmpty}>
      {isEmpty ? (
        <div className={styles.userInfoLine}>
          <span className={styles.infoLineTitle}>No advisor assigned</span>
        </div>
      ) : (
        children
      )}
    </UserInfoCard>
  )
}

export interface ClientUserAdvisorProps {
  avatar: AvatarSubject
  displayName: string
  email: string
  /**
   * Opens the advisor's details, when the current member may.
   */
  onPress?: () => void
  testId?: string
}

/**
 * An advisor assigned to the client.
 */
export function ClientUserAdvisor({
  avatar,
  displayName,
  email,
  onPress,
  testId,
}: ClientUserAdvisorProps) {
  return (
    <ListItem onPress={onPress} testId={testId}>
      <ListItemVisual>
        <Avatar size={32} subject={avatar} />
      </ListItemVisual>
      <ListItemContent>
        <ListItemTitle>{displayName}</ListItemTitle>
        <ListItemDescription>{email}</ListItemDescription>
      </ListItemContent>
    </ListItem>
  )
}

interface UserInfoCardProps {
  title: string
  children: ReactNode
  /**
   * Lets list rows run to the card's edges below the title.
   */
  listContent?: boolean
}

function UserInfoCard({ title, children, listContent }: UserInfoCardProps) {
  return (
    <div className={cx(styles.userInfoCard, listContent && styles.listContent)}>
      <div className={styles.userInfoTitle}>
        <span>{title}</span>
      </div>
      {children}
    </div>
  )
}

interface UserInfoLineProps {
  title: string
  value: ReactNode
  buttonText?: string
  buttonAction?: () => void
}

function UserInfoLine({
  title,
  value,
  buttonText,
  buttonAction,
}: UserInfoLineProps) {
  const button = buttonText && buttonAction
  const normalizeTitle = title.toLowerCase().replace(/\s+/g, '-')

  return (
    <div className={styles.userInfoLine}>
      <span className={styles.infoLineTitle}>{title}</span>
      <div className={styles.infoLineSubtitle}>
        <span
          className={styles.infoLineValue}
          data-test={`user-info-line-${normalizeTitle}-value`}
        >
          {value}
        </span>
        {button && (
          <Button
            label={buttonText}
            className={styles.infoLineButton}
            variant="text"
            onPress={buttonAction}
          />
        )}
      </div>
    </div>
  )
}
